import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import {
  Alert,
  Card,
  EnvPill,
  PageHead,
  StatusBadge,
  formatTime,
  useLoader,
} from '../components/ui';

const STATUSES = [
  '',
  'delivered',
  'sent',
  'bounced',
  'complained',
  'failed',
  'scheduled',
  'canceled',
];

export function Emails() {
  const [status, setStatus] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // Debounced so typing does not fire a query per keystroke.
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setCursor(undefined);
      setQuery(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Searching happens in the database, across every page. Filtering the loaded
  // page in the browser looks identical until the match lives on page four, and
  // then it looks like the message was never sent.
  const { data, error, loading } = useLoader(
    () =>
      api.emails.list({
        limit: 50,
        status: status || undefined,
        cursor,
        search: query || undefined,
      }),
    [status, cursor, query],
  );

  const rows = data?.data ?? [];

  return (
    <>
      <PageHead
        title="Email logs"
        subtitle="Every message this account has submitted, with its full event timeline."
      />

      {error && <Alert kind="error">{error}</Alert>}

      <Card
        tight
        title="Messages"
        actions={
          <div className="row">
            <input
              aria-label="Search by recipient or subject"
              type="text"
              placeholder="Search recipient or subject…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ width: 190 }}
            />
            <select
              aria-label="Filter by delivery status"
              value={status}
              onChange={(event) => {
                setCursor(undefined);
                setStatus(event.target.value);
              }}
              style={{ width: 150 }}
            >
              {STATUSES.map((option) => (
                <option key={option} value={option}>
                  {option === '' ? 'All statuses' : option}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Source</th>
                <th>Env</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((message) => (
                <tr
                  key={message.id}
                  className="clickable"
                  onClick={() => navigate(`/emails/${message.id}`)}
                >
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatTime(message.created_at)}
                  </td>
                  <td>
                    <StatusBadge status={message.status} />
                  </td>
                  <td className="mono">{message.to_address}</td>
                  <td className="truncate">
                    {message.subject ?? <span style={{ color: 'var(--text-faint)' }}>—</span>}
                  </td>
                  <td>{message.source}</td>
                  <td>
                    <EnvPill environment={message.environment} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="empty">{loading ? 'Loading…' : 'No messages match.'}</div>
        )}

        <div className="row" style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
          <span className="hint">
            {rows.length} shown
            {query ? ` — matching "${query}" across every page` : ''}
          </span>
          <span className="spacer" />
          {cursor && (
            <button type="button" className="btn btn-sm" onClick={() => setCursor(undefined)}>
              First page
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm"
            disabled={!data?.next_cursor}
            onClick={() => setCursor(data?.next_cursor ?? undefined)}
          >
            Next page
          </button>
        </div>
      </Card>
    </>
  );
}
